import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import DataComponent from '../../../DataComponent/DataComponent';
import CustomModal from '../../../Modal';
import CustomTooltip from '../../../Tooltip';
import FormGroup from '../../../FormGroup';
import {isMobile} from '../../../../utils/DeviceDetect';
import { withTranslation } from '../../../ChildrenContext';
import UserStore from '../../../../stores/UserStore';

const getUserTimezone = () => UserStore.getValidTimeZone();

const images = {
    eye: require('shared_img/icons/icon-eye.svg'),
    postbackResend: require('shared_img/icons/postback-resend-icon.svg'),
};

const HistoryList = ({
    list: propList,
    total,
    page,
    loading,
    headerNames,
    statuses,
    conversionStatuses,
    actionTypes,
    actionNames,
    postbacks,
    getListHistoryPostbacks,
    getListPostbacks,
    onResendPostback,
    t
}) => {
    const staticNamesHistory = t?.postbackPage?.history || {};
    const staticNameTable = t?.dataTable || {};
    const staticHeaderNames = staticNameTable.headers || {};
    const [filterState, setFilterState] = useState({});
    const localizedHeaders = useMemo(() => {
        return (headerNames || []).map((item) => {
            if (!item || typeof item !== 'object') {
                return item;
            }
            let localizedTitle = staticHeaderNames[item.keyTitle] || item.title || item.keyTitle;
            const output = {
                ...item
            };
            if (item.title === localizedTitle) {
                return output;
            }
            output.title = localizedTitle;
            return output;
        });
    }, [headerNames, staticHeaderNames, filterState]);

    const isMobileDevice = isMobile.any();

    const [state, setState] = useState({
        list: [],
        totalPages: total,
        page: page,
        loading: loading,
        modalIsVisible: false,
        resendPostbackId: null,
        resendPostbackModalIsVisible: false,
        sendAttempts: []
    });

    useEffect(() => {
        getListPostbacks();
    }, [getListPostbacks]);

    useEffect(() => {
        const list = [];
        for (let conversion of propList) {
            let conversionFiltered = {};
            let pushConversion = false;
            for (let dataField in conversion) {
                if (localizedHeaders && localizedHeaders.length > 0) {
                    const header = localizedHeaders.find(header => header.keyTitle === dataField);
                    if (header && header.visible) {
                        conversionFiltered[dataField] = conversion[dataField];
                        pushConversion = true;
                    }
                } else {
                    conversionFiltered[dataField] = conversion[dataField];
                    pushConversion = true;
                }
            }
            if (pushConversion) {
                const attempts = Array.isArray(conversion.sendAttempts) ? conversion.sendAttempts : [];
                conversionFiltered.__sendAttempts = attempts;
                conversionFiltered.sendAttempts = attempts;
                list.push(conversionFiltered);
            }
        }
        setState(prevState => ({
            ...prevState,
            list,
            totalPages: total,
            page: page,
            loading: loading
        }));
    }, [propList, total, page, loading, localizedHeaders]);

    const onDataTableAction = useCallback((state, actionType) => {
        setFilterState(state);
        getListHistoryPostbacks(state);
    }, [getListHistoryPostbacks]);

    const toggleModal = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            modalIsVisible: !prevState.modalIsVisible
        }));
    }, []);

    const onResendPostbackHandler = useCallback(() => {
        setState(prevState => {
            onResendPostback({
                resendPostbackId: prevState.resendPostbackId
            });
            return {
                ...prevState,
                resendPostbackModalIsVisible: false
            };
        });
    }, [onResendPostback]);

    const showResendPostbackModal = useCallback((id) => {
        setState(prevState => ({
            ...prevState,
            resendPostbackId: id,
            resendPostbackModalIsVisible: true
        }));
    }, []);

    const closeResendPostbackModal = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            resendPostbackModalIsVisible: false
        }));
    }, []);

    const onVisibleSendAttempts = useCallback((event) => {
        const index = event.target.closest('.history-postback__more')?.getAttribute('data-index');

        if (index === null || index === undefined) {
            return;
        }

        setState(prevState => {
            const item = prevState.list[parseInt(index, 10)];
            const attempts = item?.sendAttempts || [];
            if (!attempts.length) {
                return prevState;
            }
            return {
                ...prevState,
                sendAttempts: attempts
            };
        });

        toggleModal();
    }, [toggleModal]);

    const getList = useMemo(() => {
        let list = [];

        state.list.forEach((item, index) => {
            const attempts = item.sendAttempts || [];
            const row = { ...item };
            const actions = attempts.length > 0 ? (
            <>
                <CustomTooltip id={`postback-view-${index}`} tooltip={staticNamesHistory.more}>
                    <a className="history-postback__more view-button" href="#!"
                       onClick={onVisibleSendAttempts}
                       data-index={index}>
                        <img src={images.eye} alt=""/><span
                        className={'link-title'}>{staticNamesHistory.more}</span>
                    </a>
                </CustomTooltip>
                <CustomTooltip id={`postback-resend-${index}`} tooltip={staticNamesHistory.resend}>
                    <a className="history-postback__resend postback-resend-button" href="#!"
                       onClick={() => showResendPostbackModal(item.id)}
                    >
                        <img src={images.postbackResend} alt=""/><span
                        className={'link-title'}>{staticNamesHistory.resend}</span>
                    </a>
                </CustomTooltip>
            </>) : null;

            row.sendAttempts = `${attempts.length}`;
            row.actions = (
                <div className="row-controls">
                    {actions}
                </div>
            );

            delete row.__sendAttempts;

            list.push(row);
        });

        return list;
    }, [state.list, isMobileDevice, staticNamesHistory, onVisibleSendAttempts, showResendPostbackModal]);

    return (
        <div className={`history-postback__table ${isMobileDevice ? 'mobile-view' : ''}`}>
            <CustomModal
                visible={state.resendPostbackModalIsVisible}
                onClose={closeResendPostbackModal}
                onConfirm={onResendPostbackHandler}
            >
                <p>{staticNamesHistory.resendPostbackNoty} {'«' + state.resendPostbackId + '»'}?</p>
            </CustomModal>
            <DataComponent
                onAction={onDataTableAction}
                totalPages={state.totalPages}
                headerNames={localizedHeaders}
                headerNamesArray={false}
                autoTile={true}
                data={getList}
                loading={state.loading}
                isQuery={true}
                settings={true}
                settingKey={'postbackHistory'}
                tableBodyTitle={staticNamesHistory.title}
                className="simple-table"
                gray={true}
                fields={[
                    {
                        name: 'datepicker',
                        key: 'create',
                        label: staticNameTable.headers.createdAt,
                        // withTimezone: true,
                        dropdownLabel: staticNameTable.filter,
                        disabled: true
                    },
                    {
                        name: 'timezone',
                        standalone: true, // e.g. not bound to a datepicker
                        key: 'timezone',
                        label: t?.timezone?.title,
                        dropdownLabel: staticNameTable.filter,
                        disabled: true
                    },
                    {
                        name: 'datepicker',
                        key: 'updated',
                        label: staticNameTable.headers.updatedAt,
                        // withTimezone: true,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'datepicker',
                        key: 'conversionCreated',
                        label: staticNameTable.headers.createdAtConversion,
                        // withTimezone: true,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'datepicker',
                        key: 'conversionUpdated',
                        label: staticNameTable.headers.updatedAtConversion,
                        // withTimezone: true,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'datepicker',
                        key: 'trackCreated',
                        label: staticNameTable.headers.createdAtTrack,
                        // withTimezone: true,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'dynamic',
                        key: 'offer',
                        segment: 'Offer',
                        label: staticNameTable.headers.offerTitle,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'custom',
                        key: 'conversions',
                        label: staticNameTable.headers.conversion,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'goalTree',
                        key: 'goals',
                        nesting: 'goals',
                        label: staticNameTable.headers.goalTitle,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'goalTree',
                        key: 'goalActions',
                        label: staticNameTable.headers.goalActions,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'statuses',
                        data: statuses,
                        placeholder: staticNameTable.tips.byStatus,
                        label: staticNameTable.headers.status,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'conversionStatuses',
                        data: conversionStatuses,
                        label: staticNameTable.headers.conversionStatus,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'currentConversionStatuses',
                        data: conversionStatuses,
                        label: staticNameTable.headers.currentConversionStatuses,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'postbackIds',
                        data: postbacks,
                        label: staticNameTable.headers.conversionPostback,
                        placeholder: staticNamesHistory.placeholders.postback,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'actionTypes',
                        data: actionTypes,
                        placeholder: staticNameTable.headers.byActionType,
                        label: staticNameTable.headers.typeAction,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'multiselect',
                        key: 'actionNames',
                        data: actionNames,
                        placeholder: staticNameTable.headers.byActionName,
                        label: staticNameTable.headers.nameAction,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'custom',
                        key: 'advCids',
                        label: staticNameTable.headers.advCid,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'custom',
                        key: 'qs',
                        label: staticNameTable.headers.queryString,
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'custom',
                        key: 'utmMedium',
                        label: 'UtmMedium',
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'custom',
                        key: 'utmSource',
                        label: 'UtmSource',
                        dropdownLabel: staticNameTable.filter
                    },
                    {
                        name: 'custom',
                        key: 'utmCampaign',
                        label: 'UtmCampaign',
                        dropdownLabel: staticNameTable.filter
                    },
                    {name: 'custom', key: 'utmTerm', label: 'UtmTerm', dropdownLabel: staticNameTable.filter},
                    {
                        name: 'custom',
                        key: 'utmContent',
                        label: 'UtmContent',
                        dropdownLabel: staticNameTable.filter
                    },
                    {name: 'custom', key: 'param1', label: 'Param1', dropdownLabel: staticNameTable.filter},
                    {name: 'custom', key: 'param2', label: 'Param2', dropdownLabel: staticNameTable.filter},
                    {name: 'custom', key: 'param3', label: 'Param3', dropdownLabel: staticNameTable.filter},
                    {name: 'custom', key: 'param4', label: 'Param4', dropdownLabel: staticNameTable.filter},
                ]}
            />
            <CustomModal
                visible={state.modalIsVisible}
                onClose={toggleModal}
                title={staticNameTable.headers.sendAttempts}
                areNoControls={true}
            >
                <div className="form-horizontal send-modal-body">
                    {state.sendAttempts.map((item, index) => (
                        <div key={index}>
                            {index !== 0
                                ? <div className="hr-line-dashed"/>
                                : null
                            }
                            <FormGroup
                                label={staticNameTable.headers.sendDate}
                            >
                                <div className="form-control">{item.date}</div>
                            </FormGroup>

                            <FormGroup
                                label={staticNameTable.headers.sendRequest}
                            >
                                <div className="form-control">{item.request}</div>
                            </FormGroup>

                            <FormGroup
                                label={staticNameTable.headers.sendResponse}
                            >
                                <div className="form-control">{item.response}</div>
                            </FormGroup>
                        </div>
                    ))}
                </div>
            </CustomModal>
        </div>
    );
};

HistoryList.propTypes = {
    list: PropTypes.array,
    total: PropTypes.number,
    page: PropTypes.number,
    loading: PropTypes.bool,
    headerNames: PropTypes.array,
    statuses: PropTypes.array,
    conversionStatuses: PropTypes.array,
    actionTypes: PropTypes.array,
    actionNames: PropTypes.array,
    postbacks: PropTypes.array,
    getListHistoryPostbacks: PropTypes.func,
    getListPostbacks: PropTypes.func,
    onResendPostback: PropTypes.func
};

HistoryList.defaultProps = {
    list: [],
    total: 0,
    page: 1,
    loading: false,
    headerNames: [],
    statuses: [],
    conversionStatuses: [],
    actionTypes: [],
    actionNames: [],
    postbacks: [],
    getListHistoryPostbacks: () => {},
    getListPostbacks: () => {},
    onResendPostback: () => {}
};

export default withTranslation(HistoryList);
